import { useCallback, useEffect } from "react"

interface NavigatorUAData {
    platform: string
    getHighEntropyValues: (hints: string[]) => Promise<{ platform: string }>
}

interface NavigatorWithUAData extends Navigator {
    userAgentData?: NavigatorUAData
}

// Constants
const DATA_ATTR = "data-scroll-lock"
const isBrowser = typeof window !== "undefined"

// iOS detection, using `userAgentData` if available, otherwise `platform`
const checkIsIOS = (): boolean => {
    if (!isBrowser) return false

    const navigatorWithUAData = navigator as NavigatorWithUAData
    return navigatorWithUAData.userAgentData
        ? navigatorWithUAData.userAgentData.platform === "iOS"
        : /iPad|iPhone|iPod/.test(navigator?.platform) ||
              (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
}

const isIOS = checkIsIOS()

const isScrollAllowed = (): boolean => document.body.getAttribute(DATA_ATTR) === null

const useScrollLock = () => {
    const preventScroll = useCallback((): void => {
        if (!isBrowser || !isScrollAllowed()) {
            return
        }

        const body = document.body
        const scrollTop = window.pageYOffset
        const scrollbarGap = window.innerWidth - document.documentElement.clientWidth

        body.setAttribute(DATA_ATTR, "true")

        if (isIOS) {
            if (scrollTop) {
                body.style.top = `-${scrollTop}px`
            }
            body.style.position = "fixed"
            body.style.width = "100%"
        }
        body.style.overflow = "hidden"

        if (scrollbarGap > 0) {
            body.style.paddingRight = `${scrollbarGap}px`
        }
    }, [])

    const allowScroll = useCallback((): void => {
        if (isBrowser && !isScrollAllowed()) {
            const body = document.body
            const scrollTop = Math.abs(parseInt(body.style.top, 10))

            if (isIOS) {
                setTimeout(() => {
                    if (scrollTop) {
                        body.style.removeProperty("top")
                    }
                }, 1)
                body.style.removeProperty("position")
                body.style.removeProperty("width")
                window.scrollTo(0, scrollTop)
            }

            body.style.removeProperty("overflow")
            body.style.removeProperty("padding-right")

            body.removeAttribute(DATA_ATTR)
        }
    }, [])

    const toggleScrollLock = useCallback(
        (lock: boolean): void => {
            if (lock) {
                preventScroll()
            } else {
                allowScroll()
            }
        },
        [preventScroll, allowScroll]
    )

    // Automatically clean up scroll lock when the component using this hook unmounts
    useEffect(() => {
        return () => {
            allowScroll()
        }
    }, [allowScroll])

    return { preventScroll, allowScroll, toggleScrollLock }
}

export default useScrollLock
